<template lang="pug">
.google-review-listing
  v-container.pa-8
    .h5.mb-6 {{ $t('api_key') }}
    .text-sm.font-regular {{ $t('api_key_description') }}
    .text-sm.font-semibold.mb-4 {{ $t('api_key_description_2') }}
    template(v-if="isFetching")
      v-row(no-gutters)
        v-col(cols="8")
          v-skeleton-loader(type="article@3")
    template(v-else)
      listing-empty-state.my-4(v-if="!apiKey" iconName="$yzi-key-02" :primaryText="$t('no_api_key_title')" :secondaryText="$t('no_api_key_description')" :btnText="$t('generate_api_key')" :loading="isGenerating" @btnClicked="generateApiKey")
      template(v-else)
        .text-sm.neutral500--text.font-semibold.mb-2 {{ $t('api_key') }}
        v-text-field.round-xl(v-model="apiKey" outlined readonly hide-details)
          template(#append)
            v-btn(icon @click="copyApiKey" v-clipboard:copy="apiKey")
              v-icon $yzi-copy-02
        v-btn.button-sm.my-4(depressed color="danger500" dark @click="openRevokeApiKeyDialog = true") {{ $t('revoke_and_generate_api_key') }}
    .h5.my-6 {{ $t('integration_plugin') }}
    template(v-if="isFetching")
      v-row(no-gutters)
        v-col(cols="8")
          v-skeleton-loader(type="article@3")
    template(v-else)
      listing-empty-state.my-4(v-if="!apiKey" iconName="$yzi-key-02" :primaryText="$t('no_api_key_title')" :secondaryText="$t('no_api_key_description')" :btnText="$t('generate_api_key')" :loading="isGenerating" @btnClicked="generateApiKey")
      template(v-else)
        .text-md.font-medium.mb-2.mt-4 Shopify Integration
        .text-sm.font-regular.my-2 {{ $t('integration_plugin_shopify') }}
        .text-sm.neutral500--text.font-semibold.mb-2 Integration Link
        v-text-field.round-xl(v-model="shopify_link" outlined readonly hide-details)
          template(#append)
            v-btn(icon @click="copyApiKey" v-clipboard:copy="shopify_link")
              v-icon $yzi-copy-02
        .text-sm.font-regular.my-2 
          div.my-1 1. From your Shopify admin, go to Settings (bottom left) > Notifications.
          div.my-1 2. Scroll all the way down to the Webhooks section.
          div.my-1 3. Click the 'Create Webhook' button. A new window should appear.
          div.my-1 4. From the first drop-down, you can choose the event that will trigger 'get review' function:
          div.ml-1 - Order fulfillment: get review every time order is fulfilled.
          div.ml-1 - Order payment: get review every time an order has been paid.
          div.ml-1 - Order update: get review every time an order has been updated.
          div.my-1 5. Paste the integration link from above
          div.my-1 6. Select '2023-04' as the API version, then click 'Save' button.
        
        .text-md.font-medium.mb-2.mt-6 WooCommerce Integration
        .text-sm.font-regular.my-2 {{ $t('integration_plugin_woocommerce') }}
        div(style="width: 200px")
          v-btn.round-lg.text-sm.font-bold.text-none.pl-5.pr-4.py-3(color="basic700" outlined depressed height="48" @click="downloadPlugin")
            | Download WooCommerce Plugin
        .text-sm.font-regular.my-2 
          div.my-1 1. Download, install and activate the plugin.
          div.my-1 2. Go to Settings > Yezza Review Management.
          div.my-1 3. Insert the API Key above.
          div.my-1 4. Select which Order event triggers the 'get review' function

        
  .dialogs
    revoke-api-key-dialog(v-if="openRevokeApiKeyDialog" :open.sync="openRevokeApiKeyDialog" :loading="isGenerating" @confirm="generateApiKey")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ListingEmptyState from '@/components/review/listing/ListingEmptyState.vue'
import RevokeApiKeyDialog from '@/components/review/dialogs/RevokeApiKeyDialog.vue'

export default {
  name: 'ApiKey',
  components: {
    ListingEmptyState, RevokeApiKeyDialog
  },
  data() {
    return {
      isFetching: false,
      apiKey: null,
      shopify_link:null,
      isGenerating: false,
      openRevokeApiKeyDialog: false
    }
  },
  mounted () {
    this.fetchApiKey()
  },
  computed: {
    ...mapGetters({
      company: 'getCompany'
    }),
  },
  methods: {
    ...mapActions([
      'showMessage',
    ]),
    async fetchApiKey() {
      try {
        this.isFetching = true
        const { data } = await this.$axios.get(`/companies/${this.company.id}/show_secret_key`)
        this.apiKey = data.secret_key
        this.shopify_link = `https://review.yezza.com/shopify.php?secret_key=${data.secret_key}`
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])  
      } finally {
        this.isFetching = false
      }
    },
    async generateApiKey() {
      try {
        this.isGenerating = true
        const { data } = await this.$axios.post(`/companies/${this.company.id}/generate_secret_key`)
        this.apiKey = data.secret_key
        this.shopify_link = `https://review.yezza.com/shopify.php?secret_key=${data.secret_key}`
        this.showMessage(['success', this.$t('success_message.new_api_key_generated')])  
      } catch (error) {
        this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])  
      } finally {
        this.isGenerating = false
        this.openRevokeApiKeyDialog = false
      }
    },
    copyApiKey() {
      this.showMessage(['success', this.$t('success_message.item_copied_to_clipboard', { item: this.$t('api_key') })])
    },
    downloadPlugin () {
      let downloadUrl = `https://review.yezza.com/wp-content/uploads/2023/03/yezza-review-management.zip`
      window.open(downloadUrl)
    }
  },
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
::v-deep .v-input__append-inner {
  margin-top: 9px;
}
</style>